import React from 'react';
import { Card, Button, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { reloadOnChunkError } from '../../utils/lazyWithRetry';

const { Title, Text } = Typography;

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (reloadOnChunkError(error)) {
      return;
    }
    console.error('ErrorBoundary caught:', error, errorInfo.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
          <Card
            style={{
              maxWidth: 480,
              width: '100%',
              textAlign: 'center',
              borderColor: '#E2E8F0',
            }}
          >
            <WarningOutlined style={{ fontSize: 48, color: '#6366F1', marginBottom: 16 }} />
            <Title level={4} style={{ color: 'var(--color-text-primary)', marginBottom: 8 }}>
              Algo deu errado
            </Title>
            {isDev && this.state.error && (
              <Text
                style={{
                  display: 'block',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 24,
                  fontSize: 13,
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.message}
              </Text>
            )}
            <Button type="primary" onClick={this.handleReset}>
              Tentar novamente
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

function withErrorBoundary<P extends object>(Component: React.ComponentType<P>) {
  const Wrapped: React.FC<P> = (props) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
  Wrapped.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
}

export { ErrorBoundary, withErrorBoundary };
